import { Button, Card, Chip, Divider, Icons, LinearProgress, Stack, Typography } from '@healthinal/ui';
import { useState } from 'react';
import {
  PatientAddressDto,
  TherapyDto,
  TherapyDtoTherapyResult,
  TherapyMedicationDto,
  TherapyMedicationDtoPhase,
} from '../../../../../api/generated/model';
import { formatDateWithGermanMonth, getDifferenceInMonths } from '../../../../../utils/date.ts';
import { calculateTherapyProgress, getTherapyResultName } from '../../../../../utils/therapy.ts';
import { PatientAddressDisplay } from '../../../../components/patient-address/PatientAddressDisplay.tsx';
import { AdjustMedicationDialog } from './AdjustMedicationDialog.tsx';
import { AdjustTherapyDialog } from './AdjustTherapyDialog.tsx';
import { CreatePhaseTwoDialog } from './CreatePhaseTwoDialog.tsx';
import { CreateTherapyDialog } from './CreateTherapyDialog.tsx';

export function TherapyCard({ therapy, patientId }: { therapy: TherapyDto; patientId: string }) {
  const [isCreatePhaseTwoDialogOpen, setIsCreatePhaseTwoDialogOpen] = useState(false);
  const [isAdjustTherapyDialogOpen, setIsAdjustTherapyDialogOpen] = useState(false);
  const [isCreateTherapyDialogOpen, setIsCreateTherapyDialogOpen] = useState(false);

  return (
    <>
      <Card variant="soft">
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          {therapy ? (
            <>
              <Stack gap={1}>
                <Typography level="title-lg">
                  {formatDateWithGermanMonth(therapy.startPhase1)} {therapy.end && `- ${formatDateWithGermanMonth(therapy.end)}`}
                </Typography>
                <Stack direction="row" gap={1}>
                  {therapy.finished ? (
                    <Chip color="primary" variant="solid">
                      Beendet
                    </Chip>
                  ) : (
                    <Chip color="success" variant="solid">
                      Aktiv
                    </Chip>
                  )}
                  <Typography>
                    {therapy.startPhase1 &&
                      therapy.end &&
                      `${getDifferenceInMonths(therapy.startPhase1, therapy.end)} Monat(e)`}
                  </Typography>
                </Stack>
                <Stack>
                  {therapy.resultNote && <Typography>Bemerkung: {therapy.resultNote}</Typography>}
                  {therapy.therapyResult != TherapyDtoTherapyResult.PENDING && (
                    <Typography>{getTherapyResultName(therapy.therapyResult)}</Typography>
                  )}
                  {therapy.endReferralDoctor && <PatientAddressDisplay patientAddress={therapy.endReferralDoctor} />}
                </Stack>
              </Stack>
              <Button onClick={() => setIsAdjustTherapyDialogOpen(true)} color={'neutral'}>
                Anpassen
              </Button>
            </>
          ) : (
            <Typography level={'title-lg'}>Therapie</Typography>
          )}
        </Stack>
        <Divider />
        {therapy.startPhase1 ? (
          <Stack gap={2}>
            <Stack gap={1}>
              <Typography level="title-lg">Therapiefortschritt</Typography>
              <LinearProgress
                determinate
                value={
                  therapy.startPhase1 && therapy.end ? calculateTherapyProgress(therapy.startPhase1, therapy.end) : 0
                }
              />
              <Stack direction={'row'} gap={2}>
                {therapy.startPhase1 && (
                  <TherapyPhase
                    phase={1}
                    start={therapy.startPhase1}
                    medication={therapy.therapyMedication.filter(
                      (m) => m.phase === TherapyMedicationDtoPhase.PHASE_1 && !m.until,
                    )}
                    patientId={patientId}
                  />
                )}
                <Divider orientation="vertical" />
                {therapy.startPhase2 ? (
                  <TherapyPhase
                    phase={2}
                    start={therapy.startPhase2}
                    medication={therapy.therapyMedication.filter(
                      (m) => m.phase === TherapyMedicationDtoPhase.PHASE_2 && !m.until,
                    )}
                    patientId={patientId}
                  />
                ) : (
                  <Stack flex={1}>
                    <Stack alignItems={'flex-end'}>
                      <Button
                        variant={'outlined'}
                        onClick={() => setIsCreatePhaseTwoDialogOpen(true)}
                        color={'neutral'}>
                        Phase 2 starten
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
            <Stack direction={'row'} gap={2}>
              {therapy.phase1Doctor && <TherapyPhaseDoctor doctor={therapy.phase1Doctor} />}
              <Divider orientation="vertical" inset={'context'} />
              {therapy.phase2Doctor ? <TherapyPhaseDoctor doctor={therapy.phase2Doctor} /> : <Stack flex={1} />}
            </Stack>
          </Stack>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level="body-sm">Noch keine Therapie vorhanden.</Typography>
            {!therapy.finished && <Button onClick={() => setIsCreateTherapyDialogOpen(true)}>Therapie erfassen</Button>}
          </Stack>
        )}
      </Card>
      {therapy && (
        <CreatePhaseTwoDialog
          open={isCreatePhaseTwoDialogOpen}
          onClose={() => setIsCreatePhaseTwoDialogOpen(false)}
          currentTherapy={therapy}
        />
      )}
      {therapy && (
        <AdjustTherapyDialog
          open={isAdjustTherapyDialogOpen}
          onClose={() => setIsAdjustTherapyDialogOpen(false)}
          currentTherapy={therapy}
        />
      )}
      <CreateTherapyDialog
        open={isCreateTherapyDialogOpen}
        onClose={() => setIsCreateTherapyDialogOpen(false)}
        patientId={patientId}
      />
    </>
  );
}

function TherapyPhase({
  patientId,
  phase,
  start,
  medication,
}: {
  patientId: string;
  phase: number;
  start: string;
  medication: TherapyMedicationDto[];
}) {
  const [isAdjustMedicationDialogOpen, setIsAdjustMedicationDialogOpen] = useState(false);
  return (
    <Stack flex={1} gap={2} justifyContent={'space-between'}>
      <Stack gap={2}>
        <Stack>
          <Typography level="title-sm">Phase {phase}</Typography>
          <Typography level="body-sm">{formatDateWithGermanMonth(start)}</Typography>
        </Stack>
        <Stack>
          <Typography level="title-sm">Medikation</Typography>
          {medication.map((med) => (
            <Typography key={med.id} level={'body-sm'}>
              {med.medication.name}
            </Typography>
          ))}
        </Stack>
      </Stack>
      <div>
        <Button onClick={() => setIsAdjustMedicationDialogOpen(true)} color={'neutral'} variant={'outlined'}>
          Medikation anpassen
        </Button>
      </div>
      <AdjustMedicationDialog
        open={isAdjustMedicationDialogOpen}
        onClose={() => setIsAdjustMedicationDialogOpen(false)}
        currentTherapyMedication={medication}
        phase={phase}
        patientId={patientId}
      />
    </Stack>
  );
}

function TherapyPhaseDoctor({ doctor }: { doctor: PatientAddressDto }) {
  return (
    <Stack flex={1}>
      <Typography level={'title-sm'}>Behandelnder Arzt</Typography>
      <PatientAddressDisplay patientAddress={doctor} />
    </Stack>
  );
}
